<template>
  <app-page class="tk-page" fill lean>
    <!-- Aufbau wie die übrigen Tabellenseiten (Kundenwunsch 13.10.2026: „jetzt bitte alle shop seiten überarbeiten“):
         leichter Titel, Aktionen mit Beschriftung (Handy: Icon-Knöpfe); links Monat und Suche, rechts der Filter statt
         der Filterzeile. Tabelle mit wenigen Bildschirmspalten wie Verkäufe, ein Klick auf ein Ticket öffnet rechts den
         Detailbereich mit allen Angaben und den Aktionen (Kundenwunsch 13.10.2026) -->
    <template #actions>
      <template v-if="!handy">
        <app-button icon="refresh" @click="refreshDataGrid">Aktualisieren</app-button>
        <app-button icon="download" @click="exportToExcel">Excel-Export</app-button>
      </template>
      <template v-else>
        <app-icon-button icon="refresh" label="Aktualisieren" @click="refreshDataGrid" />
        <app-icon-button icon="download" label="Excel-Export" @click="exportToExcel" />
      </template>
    </template>
    <template #toolbar>
      <app-month-stepper v-model="month" />
      <app-search v-model="searchText" placeholder="In Tickets suchen" @input="onSearchInput" />
    </template>
    <template #toolbar-end>
      <sale-filter-menu
        :value="filters"
        :facets="facets"
        :result-count="resultCount"
        :description="filterTooltip"
        :segment-defs="filterSegments"
        :time-defs="filterTimes"
        :time-days="timeDays"
        :active-filters="activeFilterCount"
        range-label="Preis"
        :result-nouns="resultNouns"
        :empty-value="emptyTicketFilters"
        :align-right="!handy"
        @open="refreshFacets"
        @input="onFiltersInput"
      />
    </template>

    <!-- Dialoge liegen über der Seite und nehmen keinen Platz im Inhalt ein; aus dem Detailbereich verschachtelt -->
    <app-dialog v-model="confirmCancelDialog" persistent :max-width="600" nested blur>
      <template #title>{{ cancelType == "ORDER" ? "Bestellung" : "Ticket" }} stornieren</template>
      <p class="tk-text">
        Sind Sie sicher, dass Sie
        {{ cancelType == "ORDER" ? "die Bestellung" : "das Ticket" }}
        <strong v-show="cancelType == 'ORDER'"
          >#{{ cancelData ? cancelData.order_id : "" }}</strong
        >
        <strong v-show="cancelType == 'TICKET'">{{ cancelData ? ticketNo(cancelData) : "" }}</strong>
        stornieren möchten?
      </p>
      <p v-if="cancelErrorMsg" class="app-field__error tk-error" role="alert">
        <app-icon name="alert" :size="14" />{{ cancelErrorMsg }}
      </p>
      <template #actions>
        <app-button @click="confirmCancelDialog = false">Abbrechen</app-button>
        <app-button kind="danger" :loading="cancelLoading" @click="cancel">Stornieren</app-button>
      </template>
    </app-dialog>

    <!-- Umbuchen: Fahrt wählen wie auf der Seite Fahrten (ShopTripPicker, Kundenwunsch 13.10.2026); bei jedem Öffnen neu
         mit dem Monat der Fahrt des Tickets -->
    <app-dialog v-model="confirmRebookDialog" :max-width="960" nested blur>
      <template #title>Ticket {{ rebookData ? ticketNo(rebookData) : "" }} umbuchen</template>
      <shop-trip-picker v-if="confirmRebookDialog" :ticket="rebookData" @select="onTripSelected" />
      <p v-if="rebookErrorMsg" class="app-field__error tk-error" role="alert">
        <app-icon name="alert" :size="14" />{{ rebookErrorMsg }}
      </p>
      <template #actions>
        <app-button @click="confirmRebookDialog = false">Abbrechen</app-button>
        <app-button kind="primary" icon="calClock" :disabled="!rebookTripData" :loading="rebookLoading" @click="rebook">
          {{ rebookTripData ? `Auf Fahrt ${rebookTripData.trip_id} umbuchen` : "Umbuchen" }}
        </app-button>
      </template>
    </app-dialog>

    <div class="tk-gridcol">
      <!-- Tabelle füllt die Höhe über der Fußleiste (Handy: natürliche Höhe) -->
      <div class="app-grid-fill tk-grid" :class="{ 'tk-grid--auto': handy }">
        <dx-data-grid
          :ref="gridRefName"
          :height="gridHeight"
          :columns="columns"
          :data-source="ticketDataSource"
          :show-borders="false"
          :show-column-lines="false"
          :show-row-lines="true"
          :column-auto-width="false"
          :allow-column-reordering="!gridTouch"
          :allow-column-resizing="!gridTouch"
          :hover-state-enabled="!gridTouch"
          :scrolling="scrolling"
          :element-attr="gridAttr"
          :focused-row-enabled="true"
          @toolbar-preparing="onToolbarPreparing"
          @focused-row-changed="onFocusedRowChanged"
          @row-prepared="onRowPrepared"
          @content-ready="onContentReady"
        >
          <!-- Seitenwahl und Blättern in der Fußleiste (AppGridFooter) -->
          <dx-pager :visible="false" />
          <dx-paging :page-size="defaultPageSize" />

          <!-- Summe bleibt für den Excel-Export; auf dem Bildschirm steht sie in der Fußleiste (Summenzeile ausgeblendet) -->
          <dx-summary>
            <dx-total-item
              column="price"
              summary-type="sum"
              display-format="Summe: {0}"
              value-format="#######0.00 €"
            />
          </dx-summary>

          <!-- Fahrgast und Strecke einzeilig mit Ellipse -->
          <template #ellipsisTemplate="cell">
            <span class="tk-ellipsis" :title="cell.data.text">{{ cell.data.text || "–" }}</span>
          </template>

          <!-- Status als 7-px-Punkt mit Text: abgeschlossen gefüllt, offen als Ring, storniert rot -->
          <!-- Status als farbiges Etikett: grün abgeschlossen, gelb offen, rot storniert (Kundenwunsch 14.10.2026) -->
          <template #orderStatusTemplate="cell">
            <ticket-status-pill :status="cell.data.data.status" :text="cell.data.text" />
          </template>

          <!-- Vertriebskanal als Icon wie früher die Spalte „Typ“ (Kundenwunsch 13.10.2026); Name als Tooltip und für
               Screenreader -->
          <template #channelTemplate="cell">
            <span v-if="channelIcon(cell.data.data.type)" class="tk-type" :title="cell.data.text">
              <app-icon :name="channelIcon(cell.data.data.type)" :size="16" />
              <span class="app-sr-only">{{ cell.data.text }}</span>
            </span>
            <span v-else>{{ cell.data.text || "–" }}</span>
          </template>

          <!-- Bestellnummer „#919“ nur in der ersten Zeile einer Bestellung; Folgezeilen derselben Bestellung lassen sie weg
               (für Screenreader bleibt sie), die Linie dazwischen beginnt erst rechts der Spalte (onRowPrepared) -->
          <template #orderTemplate="cell">
            <span v-if="continuesOrder(cell.data)" class="app-sr-only">#{{ cell.data.data.order_id }}</span>
            <span v-else class="tk-num">#{{ cell.data.data.order_id }}</span>
          </template>

          <!-- Check-In: Haken mit „Ja“, sonst Strich -->
          <!-- letzte Änderung kurz wie „Gekauft“ -->
          <template #geaendertKopf>
            <span>Geändert</span>
          </template>
          <template #geaendertZelle="cell">
            <span class="tk-num">{{ cell.data.value ? shortTime(cell.data.value) : "–" }}</span>
          </template>

          <template #checkinTemplate="cell">
            <span v-if="cell.data.data.checked_in == 1" class="tk-checkin">
              <app-icon name="check" :size="14" class="tk-checkin__icon" />Ja
            </span>
            <span v-else class="tk-none"><span aria-hidden="true">–</span><span class="app-sr-only">Nein</span></span>
          </template>

          <!-- Preis nach R6 („13,50 €“, unabhängig von der Browsersprache); der Export behält das Zahlenformat -->
          <template #priceTemplate="cell">
            <span class="tk-num">{{ euroText(cell.data.data.price) }}</span>
          </template>
        </dx-data-grid>
      </div>
      <app-grid-footer ref="fuss" :page-sizes="pageSizes">
        <template #summary>
          <span>Summe <strong>{{ sumText("price") }}</strong></span>
          <span class="app-sep" aria-hidden="true">·</span>
          <span v-if="narrowed"><strong>{{ summaryCount }}</strong> von {{ summaryLoaded }}</span>
          <span v-else><strong>{{ summaryCount }}</strong> {{ summaryNoun }}</span>
          <!-- Summe der stornierten Tickets daneben (Kundenwunsch 14.10.2026: „bei den stornierten Tickets eine Summe am
               unteren Rand“), nur wenn unter den angezeigten welche storniert sind -->
          <template v-if="summary.canceledCount">
            <span class="app-sep" aria-hidden="true">·</span>
            <span>davon storniert <strong>{{ sumText("canceled") }}</strong> ({{ canceledCountText }})</span>
          </template>
        </template>
      </app-grid-footer>
    </div>

    <template #detail>
      <app-detail-pane
        :open="!!selectedTicket"
        medium
        icon="ticket"
        title="Ticket"
        close-label="Ticket schließen"
        @close="closeDetail"
        @layout-change="onDetailLayout"
      >
        <template #subtitle>
          <span v-if="selectedTicket">{{ ticketNo(selectedTicket) }}</span>
          <app-tag v-if="selectedTicket && selectedTicket.status == 'CANCELED'">Storniert</app-tag>
        </template>
        <shop-ticket-detail
          v-if="selectedTicket"
          :ticket="selectedTicket"
          :ticket-no="ticketNo(selectedTicket)"
          :shop-url="shopUrl"
        />
        <!-- alle Aktionen einheitlich im Fuß (Kundenwunsch 13.10.2026: „zwei action buttons im footer und einen im detail
             content das muss bitte einheitlich sein“): „Umbuchen …“ und „Stornieren“ mit Auswahl Ticket oder ganze
             Bestellung; bei stornierten Tickets nur noch „Bestellung stornieren …“, bei mobil verkauften keine -->
        <template v-if="selectedActionable" #footer>
          <template v-if="selectedTicket.status != 'CANCELED'">
            <app-button icon="calClock" @click="openRebookDialog(selectedTicket)">Umbuchen …</app-button>
            <v-menu top left offset-y nudge-top="4" content-class="app-menu">
              <template v-slot:activator="{ on, attrs }">
                <app-button icon="ticketX" chevron v-bind="attrs" v-on="on">Stornieren</app-button>
              </template>
              <div class="tk-menu" role="menu" aria-label="Stornieren">
                <button
                  type="button"
                  class="v-list-item app-menu__item"
                  role="menuitem"
                  @click="openCancelDialog(selectedTicket, 'TICKET')"
                >
                  <span class="app-menu__text">Nur Ticket {{ ticketNo(selectedTicket) }} …</span>
                </button>
                <button
                  type="button"
                  class="v-list-item app-menu__item"
                  role="menuitem"
                  @click="openCancelDialog(selectedTicket, 'ORDER')"
                >
                  <span class="app-menu__text">Ganze Bestellung #{{ selectedTicket.order_id }} …</span>
                </button>
              </div>
            </v-menu>
          </template>
          <app-button v-else icon="ticketX" @click="openCancelDialog(selectedTicket, 'ORDER')">
            Bestellung stornieren …
          </app-button>
        </template>
      </app-detail-pane>
    </template>
  </app-page>
</template>

<script>
import {
  DxDataGrid,
  DxPaging,
  DxPager,
  DxSummary,
  DxTotalItem,
} from "devextreme-vue/data-grid";
import DataGridMixin from "@/mixins/DataGridMixin";
import CustomStore from "devextreme/data/custom_store";
import axios from "axios";
import moment from "moment";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import saveAs from "file-saver";
import ShopTripPicker from "@/components/shop/ShopTripPicker";
import ShopTicketDetail from "@/components/shop/ShopTicketDetail";
import TicketStatusPill from "@/components/shop/TicketStatusPill";
import { formatEuro, formatNumber, formatDateTimeShort, plural } from "@/utils/format";
import query from "devextreme/data/query";
import SaleFilterMenu from "@/components/sales/SaleFilterMenu";
import { emptyFilters, activeCount, buildPredicate, buildFacets, buildTimeDays, describeFilters, NO_VALUE_TEXT } from "@/components/sales/saleFilters";
import { TICKET_FILTER_CONFIG, TICKET_STATUS_TEXT, TICKET_TYPE_TEXT, TICKET_TYPE_ICON } from "@/components/shop/shopFilters";

// Bildschirmspalten in dieser Reihenfolge (Kundenwünsche 13.10.2026: „viel zu viele spalten“, Vertriebskanal als Icon,
// Bestellnummer vorn neben der Ticketnummer); bei offenem Detailbereich ohne „Gekauft“, „Kanal“, „Fahrt“ und „Check-In“
// (stehen dann rechts im Detail)
// seit 14.10.2026 mit „Geändert“ (letzte Änderung, Kundenwunsch); die Fahrtnummer steht dafür nur noch im Detail, in
// der Suche und im Filter
// Status weiter vorn direkt nach der Ticketnummer (Kundenwunsch 14.10.2026)
const SCREEN_COLUMNS = [
  "order_id", "id", "status", "purchased", "modification_time", "type", "passenger", "route", "departure", "checked_in",
  "price",
];
// mit offenem Detail (seit 14.10.2026 breiter) ohne Preis: der steht groß oben im Detail
const SCREEN_COLUMNS_DETAIL = ["order_id", "id", "status", "passenger", "route", "departure"];
// Excel-Export wie bisher mit allen Datenspalten in der bisherigen Reihenfolge (ohne die frühere Spalte „Aktionen“)
const EXPORT_COLUMNS = [
  "status", "checked_in", "id", "order_id", "provider", "date", "time", "modification_time", "type", "fullname",
  "trip_id", "trip_day", "trip_start", "start_location_name", "end_location_name", "seats", "price", "vat_rate",
  "first_name", "last_name", "phone", "mail", "flight_number", "paypal_order_id", "paypal_capture_id",
];

function present(value) {
  return value !== null && value !== undefined && value !== "";
}

export default {
  components: {
    DxDataGrid,
    DxPager,
    DxPaging,
    DxSummary,
    DxTotalItem,
    ShopTripPicker,
    ShopTicketDetail,
    TicketStatusPill,
    SaleFilterMenu,
  },
  mixins: [DataGridMixin],
  computed: {
    handy() {
      return !!(this.$dichte && this.$dichte.handy);
    },
    // Handy: AppPage fill wirkt dort nicht, das Grid wächst mit seinen Zeilen; sonst füllt es die Höhe
    gridHeight() {
      return this.handy ? undefined : "100%";
    },
    // Fußleiste: Anzahl nach Filter und Suche, „4 von 24 …“ solange eingeschränkt
    narrowed() {
      return this.summary.count !== this.summary.loaded;
    },
    summaryCount() {
      return formatNumber(this.summary.count);
    },
    summaryLoaded() {
      return formatNumber(this.summary.loaded) + " " + plural(this.summary.loaded, "Ticket", "Tickets");
    },
    summaryNoun() {
      return plural(this.summary.count, "Ticket", "Tickets");
    },
    // „7 Tickets“ bzw. „1 Ticket“ (stornierte unter den angezeigten)
    canceledCountText() {
      const n = this.summary.canceledCount || 0;
      return formatNumber(n) + " " + plural(n, "Ticket", "Tickets");
    },
    activeFilterCount() {
      return activeCount(this.filters, TICKET_FILTER_CONFIG);
    },
    filterTooltip() {
      const parts = describeFilters(this.filters, this.filterLabel, 3, TICKET_FILTER_CONFIG);
      return parts.length ? parts.join(" · ") : null;
    },
    // Kürzel des Shops (Ticketnummer „SHOP-5039“, Link zur Bestellung)
    shopUrl() {
      const customer = this.$store.state.customer;
      return customer && customer.shop_url ? String(customer.shop_url) : "";
    },
    // Aktionen wie bisher nur für Tickets, die nicht mobil verkauft wurden
    selectedActionable() {
      return !!this.selectedTicket && this.selectedTicket.type != "MOBILE";
    },
  },
  mounted() {
    // Reihenfolge und Sichtbarkeit der Bildschirmspalten (die Spalte „Geändert“ steht in der Definition bei den
    // verborgenen Exportspalten)
    this.$nextTick(() => this.applyColumns(this.screenColumns()));
  },
  watch: {
    month: function () {
      this.closeDetail();
      this.refreshDataGrid();
    },
    // offener Detailbereich: weniger Spalten, sonst wird die Tabelle neben dem Detail zu schmal
    selectedTicket(ticket, previous) {
      if (!!ticket !== !!previous) {
        this.applyColumns(this.screenColumns());
      }
    },
  },
  data() {
    return {
      gridRefName: "shopTicketDataGrid",
      month: new Date().toISOString().substr(0, 7),
      searchText: "",
      // Filter-Panel (Kundenwunsch 13.10.2026): Zustand, Werte der Listen, Treffer nach Filter und Suche
      filters: emptyFilters(TICKET_FILTER_CONFIG),
      filterSegments: TICKET_FILTER_CONFIG.segments,
      filterTimes: TICKET_FILTER_CONFIG.times,
      timeDays: {},
      resultNouns: ["Ticket", "Tickets"],
      facets: [],
      resultCount: null,
      summary: { count: 0, loaded: 0, sums: {} },
      // Zeiger auf den Zeilen: ein Klick öffnet den Detailbereich (Kundenwunsch 13.10.2026)
      gridAttr: { class: "app-grid--detail" },
      // Detailbereich: gewähltes Ticket (eingefroren)
      selectedTicket: null,
      confirmCancelDialog: false,
      confirmRebookDialog: false,
      cancelData: null,
      rebookData: null,
      rebookTripData: null,
      cancelLoading: false,
      rebookLoading: false,
      cancelErrorMsg: null,
      rebookErrorMsg: null,
      cancelType: null,
      // Bildschirmspalten (Kundenwunsch 13.10.2026, wie Verkäufe und Shop-Fahrten einzeilig): Bestellung, Ticket, Gekauft,
      // Kanal (Icon), Fahrgast, Strecke, Abfahrt, Fahrt, Status, Check-In, Preis; sortiert nach Bestellung und Ticket
      // absteigend, damit die Tickets einer Bestellung untereinander stehen. Alle übrigen Datenspalten bleiben verborgen
      // für Suche und Excel-Export (EXPORT_COLUMNS) und stehen im Detailbereich.
      columns: [
        {
          // Bestellnummer vorn neben der Ticketnummer (Kundenwünsche 13.10.2026: „zwingend erforderlich“, „zeilen mit selber
          // bestellnummer zusammenfassen indem man die bestellnummer an den anfang stellt“); „#919“ nur auf dem Bildschirm
          // und nur in der ersten Zeile einer Bestellung, der Export behält die Zahl
          caption: "Bestellung",
          dataField: "order_id",
          width: 124,
          alignment: "left",
          cellTemplate: "orderTemplate",
          cssClass: "tk-order",
          sortOrder: "desc",
          sortIndex: 0,
        },
        {
          caption: "Ticket",
          dataField: "id",
          width: 116,
          calculateDisplayValue: (rowData) => this.ticketNo(rowData),
          calculateSortValue: (rowData) => rowData.id,
          sortOrder: "desc",
          sortIndex: 1,
          alignment: "left",
        },
        {
          name: "purchased",
          caption: "Gekauft",
          width: 112,
          // „12.10., 10:00“ (das Jahr steht im Monat der Werkzeugleiste)
          calculateCellValue: (row) => (present(row.time) ? formatDateTimeShort(row.time) : row.date),
          calculateSortValue: (row) => row.time,
          cssClass: "tk-num",
        },
        {
          dataField: "type",
          caption: "Kanal",
          width: 64,
          alignment: "center",
          cellTemplate: "channelTemplate",
          lookup: {
            dataSource: {
              store: {
                type: "array",
                data: Object.keys(TICKET_TYPE_TEXT).map((type) => ({ type, name: TICKET_TYPE_TEXT[type] })),
                key: "type",
              },
            },
            valueExpr: "type",
            displayExpr: "name",
          },
        },
        {
          name: "passenger",
          caption: "Fahrgast",
          minWidth: 88,
          calculateCellValue: (row) => [row.first_name, row.last_name].filter(present).join(" "),
          cellTemplate: "ellipsisTemplate",
        },
        {
          name: "route",
          caption: "Strecke",
          minWidth: 100,
          calculateCellValue: (row) =>
            [row.start_location_name, row.end_location_name].filter(present).join(" → "),
          cellTemplate: "ellipsisTemplate",
        },
        {
          name: "departure",
          caption: "Abfahrt",
          width: 132,
          calculateCellValue: (row) => this.departureText(row),
          calculateSortValue: (row) => `${row.trip_day || ""} ${row.trip_start || ""}`,
          cssClass: "tk-num",
        },
        {
          dataField: "trip_id",
          caption: "Fahrt",
          width: 72,
          alignment: "left",
        },
        {
          dataField: "status",
          caption: "Status",
          width: 140,
          cssClass: "tk-statuscol",
          cellTemplate: "orderStatusTemplate",
          lookup: {
            dataSource: {
              store: {
                type: "array",
                data: [
                  { state: "OPEN", name: TICKET_STATUS_TEXT.OPEN },
                  { state: "FINISHED", name: TICKET_STATUS_TEXT.FINISHED },
                  { state: "CANCELED", name: TICKET_STATUS_TEXT.CANCELED },
                ],
                key: "state",
              },
            },
            valueExpr: "state",
            displayExpr: "name",
          },
        },
        {
          caption: "Check-In",
          dataField: "checked_in",
          width: 88,
          alignment: "left",
          cellTemplate: "checkinTemplate",
        },
        {
          dataField: "price",
          caption: "Preis",
          width: 88,
          alignment: "right",
          dataType: "number",
          format: "######0.00 €",
          cellTemplate: "priceTemplate",
        },
        // ---- verborgene Datenspalten (Suche, Excel-Export)
        {
          // Suche nach Ticket- und Bestellnummer als Text (Kundenrückmeldung 14.10.2026: „wir können die Tickets nur nach
          // der Bestellnummer suchen und nicht nach der Ticketnummer“): DevExtreme sucht in Zahlenspalten nur die genaue
          // Zahl und nie die Anzeige „KÜRZEL-5039“; hier findet „5039“, „503“, „kürzel-5039“, „919“ und „#919“. Nicht im
          // Export (fehlt in EXPORT_COLUMNS).
          name: "nummernSuche",
          caption: "Ticket- und Bestellnummer",
          visible: false,
          dataType: "string",
          // ohne dataField ist eine Spalte bei DevExtreme sonst nicht filterbar und damit nicht durchsucht
          allowFiltering: true,
          allowSearch: true,
          calculateCellValue: (rowData) => this.ticketNo(rowData) + " #" + rowData.order_id,
        },
        {
          name: "provider",
          caption: "Zahlungsprovider",
          dataField: "provider",
          visible: false,
          calculateCellValue: (rowData) => {
            if (rowData.paypal_order_id || rowData.paypal_capture_id) {
              return "PAYPAL";
            } else if (rowData.stripe_payment_intent) {
              return "STRIPE";
            } else {
              return "";
            }
          },
        },
        {
          dataField: "date",
          caption: "Datum",
          visible: false,
        },
        {
          dataField: "time",
          caption: "Zeit",
          dataType: "datetime",
          format: "HH:mm:ss",
          visible: false,
        },
        {
          // Bildschirm „Geändert“ mit „13.10., 14:22“ (Kopf und Zelle per Vorlage); Excel wie bisher „Letzte Änderung“ mit
          // Datum und Uhrzeit
          dataField: "modification_time",
          caption: "Letzte Änderung",
          dataType: "datetime",
          format: "dd.MM.yyyy, HH:mm:ss",
          width: 112,
          visible: false,
          headerCellTemplate: "geaendertKopf",
          cellTemplate: "geaendertZelle",
        },
        {
          dataField: "fullname",
          caption: "Verkäufer",
          visible: false,
        },
        {
          dataField: "trip_day",
          caption: "Fahrtdatum",
          dataType: "date",
          format: "dd.MM.yyyy",
          visible: false,
        },
        {
          dataField: "trip_start",
          caption: "Fahrtstart",
          visible: false,
        },
        {
          dataField: "start_location_name",
          caption: "Von",
          visible: false,
        },
        {
          dataField: "end_location_name",
          caption: "Nach",
          visible: false,
        },
        {
          dataField: "seats",
          caption: "Plätze",
          visible: false,
        },
        {
          dataField: "vat_rate",
          caption: "MwSt.",
          alignment: "right",
          dataType: "number",
          format: "#0.00 '%'",
          visible: false,
        },
        {
          dataField: "first_name",
          caption: "Vorname",
          visible: false,
        },
        {
          dataField: "last_name",
          caption: "Nachname",
          visible: false,
        },
        {
          dataField: "phone",
          caption: "Telefon",
          visible: false,
        },
        {
          dataField: "mail",
          caption: "E-Mail",
          visible: false,
        },
        {
          dataField: "flight_number",
          caption: "Flug",
          visible: false,
        },
        {
          dataField: "paypal_order_id",
          caption: "Paypal Order-ID",
          visible: false,
        },
        {
          dataField: "paypal_capture_id",
          caption: "Paypal Capture-ID",
          visible: false,
        },
      ],
      ticketDataSource: new CustomStore({
        key: ["uuid"],
        // Frueher wurden 20 Requests mit fest verdrahteten Offsets bis 47500
        // parallel abgeschickt - unabhaengig davon, wie viele Tickets der Monat
        // ueberhaupt hat. Der groesste Monat 2026 hatte 5628 Tickets, es liefen
        // also 17 der 20 Abfragen ins Leere und belegten trotzdem jeweils rund
        // eine Sekunde Datenbankzeit. Weil alle gleichzeitig auf denselben
        // Cluster trafen, dauerte jede einzelne dadurch etwa sechs Sekunden.
        //
        // Jetzt in Wellen zu vier Seiten: geladen wird nur, solange eine Welle
        // vollstaendig gefuellt zurueckkommt. Fuer einen typischen Monat ist
        // nach der ersten Welle Schluss. Nebenbei ist die frueher bei 50000
        // Tickets liegende stille Obergrenze weg.
        load: async () => {
          const limit = 2500;
          const waveSize = 4;
          const base =
            `${this.$store.state.baseUrl}shopticket` +
            `?&month=${moment(this.month + "-01").format("MM")}` +
            `&year=${moment(this.month + "-01").format("YYYY")}` +
            `&limit=${limit}`;

          const rows = [];
          for (let offset = 0; ; offset += limit * waveSize) {
            const wave = await axios.all(
              Array.from({ length: waveSize }, (unused, i) =>
                axios.get(`${base}&offset=${offset + i * limit}`)
              )
            );

            let waveComplete = true;
            for (const response of wave) {
              rows.push(...response.data);
              if (response.data.length < limit) waveComplete = false;
            }
            if (!waveComplete) {
              this._rows = rows;
              this.refreshFacets();
              this.syncSelectedTicket();
              return rows;
            }
          }
        },
      }),
    };
  },
  methods: {
    // ---------------------------------------------------------------- Filter-Panel (Kundenwunsch 13.10.2026)
    emptyTicketFilters() {
      return emptyFilters(TICKET_FILTER_CONFIG);
    },
    filterLabel(listKey, key) {
      if (key === "") {
        return NO_VALUE_TEXT;
      }
      if (listKey === "status") {
        return TICKET_STATUS_TEXT[key] || key;
      }
      if (listKey === "types") {
        return TICKET_TYPE_TEXT[key] || key;
      }
      if (listKey === "providers") {
        return key === "PAYPAL" ? "PayPal" : key === "STRIPE" ? "Stripe" : key;
      }
      return key;
    },
    shortTime(value) {
      return formatDateTimeShort(value);
    },
    refreshFacets() {
      this.facets = Object.freeze(buildFacets(this._rows || [], this.filters, this.filterLabel, TICKET_FILTER_CONFIG));
      this.timeDays = Object.freeze(buildTimeDays(this._rows || [], TICKET_FILTER_CONFIG));
    },
    // Filter als Prüffunktion auf der Datenquelle wie bei Verkäufen (jedes Mal eine neue Funktion)
    onFiltersInput(filters) {
      this.filters = filters || this.emptyTicketFilters();
      this.applyFilters();
      this.refreshFacets();
    },
    applyFilters() {
      const grid = this.getGrid();
      if (grid) {
        const match = buildPredicate(this.filters, TICKET_FILTER_CONFIG);
        grid.filter(match ? [match, "=", true] : null);
      }
    },
    onContentReady(e) {
      const grid = e.component;
      const count = Math.max(Number(grid.totalCount()) || 0, 0);
      this.resultCount = count;
      const rows = this._rows || [];
      let visible = rows;
      const filter = grid.getCombinedFilter();
      if (filter && rows.length) {
        try {
          visible = query(rows).filter(filter).toArray();
        } catch (err) {
          visible = rows;
        }
      }
      const sums = {};
      sums.price = visible.reduce((sum, row) => sum + (Number(row.price) || 0), 0);
      const canceled = visible.filter((row) => row.status === "CANCELED");
      sums.canceled = canceled.reduce((sum, row) => sum + (Number(row.price) || 0), 0);
      this.summary = { count, loaded: Math.max(rows.length, count), sums, canceledCount: canceled.length };
      if (this.$refs.fuss) {
        this.$refs.fuss.update(grid);
      }
    },
    sumText(key) {
      return formatEuro(this.summary.sums[key] || 0);
    },
    onSearchInput() {
      this.getGrid().searchByText(this.searchText || "");
    },
    getGrid() {
      const ref = this.$refs[this.gridRefName];
      return ref && ref.instance;
    },
    // ---------------------------------------------------------------- Bildschirmspalten
    // „SHOP-5039“ (ohne Kürzel nur die Nummer)
    ticketNo(row) {
      return this.shopUrl ? `${this.shopUrl.toUpperCase()}-${row.id}` : String(row.id);
    },
    // „Di, 13.10. 06:15“
    departureText(row) {
      const day = present(row.trip_day) ? moment(row.trip_day).locale("de") : null;
      return [
        day && day.isValid() ? day.format("dd, DD.MM.") : null,
        present(row.trip_start) ? String(row.trip_start).substr(0, 5) : null,
      ].filter(Boolean).join(" ");
    },
    euroText(value) {
      return present(value) ? formatEuro(Number(value)) : "–";
    },
    channelIcon(type) {
      return TICKET_TYPE_ICON[type] || null;
    },
    // Tickets einer Bestellung zusammenfassen (Kundenwunsch 13.10.2026): gehört die sichtbare Nachbarzeile zur selben
    // Bestellung? Greift nur, wo die Zeilen nach Sortierung, Filter und Seite direkt untereinander stehen.
    sameOrderAt(rows, index, orderId) {
      const row = rows[index];
      return !!row && row.rowType === "data" && !!row.data && present(orderId) && row.data.order_id === orderId;
    },
    continuesOrder(cellInfo) {
      const rows = cellInfo.component.getVisibleRows();
      return this.sameOrderAt(rows, cellInfo.rowIndex - 1, cellInfo.data.order_id);
    },
    // Zeile mit derselben Bestellung darunter: Linie unter der Bestellspalte ausblenden (eingerückte Trennlinie)
    onRowPrepared(e) {
      if (e.rowType !== "data" || !e.data) {
        return;
      }
      const el = e.rowElement && e.rowElement.get ? e.rowElement.get(0) : e.rowElement;
      if (!el || !el.classList) {
        return;
      }
      const rows = e.component.getVisibleRows();
      el.classList.toggle("tk-order-more", this.sameOrderAt(rows, e.rowIndex + 1, e.data.order_id));
    },
    screenColumns() {
      return this.selectedTicket ? SCREEN_COLUMNS_DETAIL : SCREEN_COLUMNS;
    },
    // genau diese Spalten in dieser Reihenfolge sichtbar, alle übrigen verborgen
    applyColumns(names) {
      const grid = this.getGrid();
      if (!grid) {
        return;
      }
      grid.beginUpdate();
      this.columns.forEach((column) => {
        const name = column.name || column.dataField;
        const index = names.indexOf(name);
        grid.columnOption(name, "visible", index !== -1);
      });
      names.forEach((name, index) => grid.columnOption(name, "visibleIndex", index));
      // die letzte Spalte hat rechts 20 statt 12 px Innenabstand (devextreme.css): Abfahrt als letzte Spalte (offenes
      // Detail, ohne Preis) 8 px breiter, sonst steht „Mo, 12.10. 06…“
      grid.columnOption("departure", "width", names[names.length - 1] === "departure" ? 140 : 132);
      grid.endUpdate();
    },
    // ---------------------------------------------------------------- Detailbereich
    onFocusedRowChanged(e) {
      const row = e.row;
      if (!row || row.rowType !== "data" || !row.data) {
        return;
      }
      if (this.selectedTicket && this.selectedTicket.uuid === row.data.uuid) {
        return;
      }
      this.selectedTicket = Object.freeze(Object.assign({}, row.data));
    },
    // nach dem Neuladen den Stand des gewählten Tickets übernehmen (z. B. „Storniert“ nach dem Stornieren)
    syncSelectedTicket() {
      if (!this.selectedTicket) {
        return;
      }
      const fresh = (this._rows || []).find((row) => row.uuid === this.selectedTicket.uuid);
      if (fresh) {
        this.selectedTicket = Object.freeze(Object.assign({}, fresh));
      }
    },
    closeDetail() {
      this.selectedTicket = null;
      const grid = this.getGrid();
      if (grid && grid.option("focusedRowIndex") !== -1) {
        grid.option("focusedRowIndex", -1);
      }
    },
    onDetailLayout() {
      this.$nextTick(() => {
        const grid = this.getGrid();
        if (grid) {
          grid.updateDimensions();
        }
      });
    },
    // ---------------------------------------------------------------- Aktionen (unverändert)
    openCancelDialog(ticketData, cancelType) {
      this.cancelType = cancelType;
      this.cancelErrorMsg = null;
      this.cancelData = ticketData;
      this.confirmCancelDialog = true;
    },
    openRebookDialog(ticketData) {
      this.rebookErrorMsg = null;
      this.rebookData = ticketData;
      this.rebookTripData = null;
      this.confirmRebookDialog = true;
    },
    async cancel() {
      try {
        this.cancelLoading = true;

        const cancelData = {
          action: "CANCEL",
        };

        if (this.cancelType == "ORDER") {
          cancelData.order_id = this.cancelData.order_id;
        } else {
          cancelData.order_id = this.cancelData.order_id;
          cancelData.ticket_id = this.cancelData.id;
        }

        await axios.put(`${this.$store.state.baseUrl}shopticket`, cancelData);
        this.confirmCancelDialog = false;
        this.cancelData = null;
        this.refreshDataGrid();
      } catch (e) {
        this.cancelErrorMsg = e.message;
      } finally {
        this.cancelLoading = false;
      }
    },
    async rebook() {
      if (!this.rebookTripData || this.rebookLoading) {
        return;
      }
      this.rebookLoading = true;
      try {
        await axios.put(`${this.$store.state.baseUrl}shopticket`, {
          action: "REBOOK",
          ticket_id: this.rebookData.id,
          trip_id: this.rebookTripData.trip_id,
        });
        this.confirmRebookDialog = false;
        this.rebookData = null;
        this.refreshDataGrid();
      } catch (e) {
        this.rebookErrorMsg = e.message;
      } finally {
        this.rebookLoading = false;
      }
    },
    refreshDataGrid() {
      const grid = this.getGrid();
      if (grid) {
        grid.refresh();
      }
    },
    // Excel wie bisher mit allen Datenspalten (EXPORT_COLUMNS), danach wieder die Bildschirmspalten
    async exportToExcel() {
      const grid = this.getGrid();
      if (!grid) {
        return;
      }
      this.applyColumns(EXPORT_COLUMNS);
      try {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("Tickets");
        await exportDataGrid({ component: grid, worksheet });
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: "application/octet-stream" }), "Tickets.xlsx");
      } finally {
        this.applyColumns(this.screenColumns());
      }
    },
    // Fahrt aus ShopTripPicker (null = keine bzw. nicht wählbar)
    onTripSelected(trip) {
      this.rebookTripData = trip;
    },
    onToolbarPreparing(e) {
      // Remove all default toolbar items; die leere Leiste ganz ausblenden (sonst zweite Linie über dem Tabellenkopf)
      e.toolbarOptions.items = [];
      e.toolbarOptions.visible = false;
    },
  },
};
</script>

<style scoped>
/* Tabelle und Fußleiste als Spalte links vom Detailbereich (wie Verkäufe und Shop-Fahrten) */
.tk-gridcol {
  flex: 999 1 560px;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
/* Handy: Grid in natürlicher Höhe */
.tk-grid.tk-grid--auto {
  flex: none;
}
/* Summenzeile nur für den Export; auf dem Bildschirm steht die Summe in der Fußleiste */
.tk-page ::v-deep .dx-datagrid-total-footer {
  display: none;
}
p.tk-text {
  margin: 0;
}
p.tk-error {
  margin-top: 12px;
}
.tk-gridcol ::v-deep .tk-num {
  font-variant-numeric: tabular-nums;
}
/* Fahrgast und Strecke einzeilig mit Ellipse */
.tk-gridcol ::v-deep .tk-ellipsis {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* Status-Etikett (TicketStatusPill): rechts 8 statt 12 px Innenabstand und keine Ellipse, damit „Abgeschlossen“ in
   die 140er-Spalte passt */
.tk-gridcol ::v-deep .dx-datagrid .dx-row > td.tk-statuscol {
  padding-right: 8px;
  text-overflow: clip;
}
/* Sortierung (Bestellung, dann Ticket) ohne Ziffern im Kopf; die Pfeile bleiben */
.tk-gridcol ::v-deep .dx-sort-index-icon {
  display: none;
}
/* Tickets einer Bestellung: unter der Bestellspalte keine Linie, solange die nächste Zeile zur selben Bestellung gehört
   (auch in Fokus- und Auswahlzeile) */
.tk-gridcol ::v-deep .dx-datagrid .dx-datagrid-rowsview .dx-data-row.tk-order-more > td.tk-order {
  border-bottom-color: transparent;
}
/* Vertriebskanal: Icon --ink-2 wie früher */
.tk-type {
  display: inline-flex;
  align-items: center;
  color: var(--ink-2);
  vertical-align: middle;
}
/* Menü „Stornieren“ im Fuß des Detailbereichs */
.tk-menu {
  display: flex;
  flex-direction: column;
}
.tk-checkin {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}
.tk-checkin__icon {
  color: var(--green-dot);
}
.tk-none {
  color: var(--ink-3);
}
</style>
